import { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import { toast } from "react-toastify";

import api from "../../services/api";
import Template from "../../template";
import { useAuth } from "../../context/auth";

import * as S from "./styled";

/**
  normaliza flags vindas do backend:
  aceita 1/0, "1"/"0", true/false, null/undefined
 */
const toFlag = (v) => {
  if (v === true) return true;
  if (v === false) return false;

  const n = Number(v);
  if (Number.isNaN(n)) return false;

  return n === 1;
};

function MinhasRedacoes() {
  const { user, isLoadingUser } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = useState(true);

  // enem/normal/ia (vem tudo do /minhas-redacoes)
  const [redacoes, setRedacoes] = useState([]);

  // uerj (/minhas-redacoes/uerj)
  const [redacoesUERJ, setRedacoesUERJ] = useState([]);

  // filtros
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [tipoFiltro, setTipoFiltro] = useState("todos");

  // URL param para pré-selecionar filtros (ex: /minhas-redacoes?tipo=ia)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tipoParam = searchParams.get("tipo");

    if (tipoParam === "ia") setTipoFiltro("ia");
    if (tipoParam === "uerj") setTipoFiltro("uerj");
    if (tipoParam === "normal") setTipoFiltro("normal");
  }, [location.search]);

  const isPresencial = user?.plano === "PRESENCIAL";
  const isTeste = user?.plano === "TESTE";
  const isPlanoIAPago = !isPresencial && !isTeste; // “pago” aqui é só para UI/CTA (sem depender de lista fixa)

/** 
   --------------------
   Helpers (formatação)
   --------------------
*/

  const formatDateOnlyBR = (d) => {
    if (!d) return "---";
    try {
      return new Date(d).toLocaleDateString("pt-BR");
    } catch {
      return "---";
    }
  };

  const formatTimeBR = (d) => {
    if (!d) return "---";
    try {
      return new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "---";
    }
  };

  // regra antiga: validar expiração por data limite + hora
  const validaDataClasse = (dataTema, horaTema) => {
    if (!dataTema || !horaTema) return false;

    const now = new Date();
    const arr = `${dataTema} ${horaTema}`.split(/[- :]/);

    // yyyy-mm-dd hh:mm:ss
    const validarData = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5]);

    return validarData <= now;
  };

  const trazerTotalENEM = (redacao) => {
    const comp1 = Number(redacao.competencia1) || 0;
    const comp2 = Number(redacao.competencia2) || 0;
    const comp3 = Number(redacao.competencia3) || 0;
    const comp4 = Number(redacao.competencia4) || 0;
    const comp5 = Number(redacao.competencia5) || 0;

    const total = comp1 + comp2 + comp3 + comp4 + comp5;

    if (redacao.falta) return 0;

    // regra de "-" quando não há notas
    if (
      !redacao.competencia1 &&
      !redacao.competencia2 &&
      !redacao.competencia3 &&
      !redacao.competencia4 &&
      !redacao.competencia5
    ) {
      return "-";
    }

    if (Number.isNaN(total)) return "-";
    if (total === 0) return 0;

    return Math.round(total);
  };

  const trazerTotalUERJ = (r) => {
    if (!r) return "-";
    if (r.status !== "CORRIGIDA") return "-";

    const total =
      (r.adequacao_tema || 0) +
      (r.tipo_texto || 0) +
      (r.argumentacao || 0) +
      (r.periodo_coesao || 0) +
      (r.modalidade || 0);

    return Math.round(total);
  };

  // =========================
  // Loaders
  // =========================

  const loadRedacoes = async () => {
    if (!user || !user.id) {
      setRedacoes([]);
      return;
    }

    try {
      const response = await api.get(`/minhas-redacoes`);

      if (response.data && response.data.data) {
        const redacoesFormatadas = response.data.data.map((redacao) => {
          const corrigida = toFlag(redacao.corrigida);
          const finalizada = toFlag(redacao.aluno_enviou);
          const ia = toFlag(redacao.ia);

          return {
            id: redacao.id_redacao,
            nome: redacao.tema_redacao,
            entrega: redacao.data_limite,
            hora: redacao.hora_limite,

            competencia1: redacao.c1_nota,
            competencia2: redacao.c2_nota,
            competencia3: redacao.c3_nota,
            competencia4: redacao.c4_nota,
            competencia5: redacao.c5_nota,

            falta: false,

            // compatível com (1/0) e defensivo para string/boolean/null
            corrigida,
            finalizada,
            tipo: ia ? "IA" : "Normal",

            created_at: redacao.created_at,
            url_redacao: redacao.url_redacao,
            // após leftJoin pode vir null/undefined
            turma: redacao.turma || null,
          };
        });

        setRedacoes(redacoesFormatadas);
      } else {
        setRedacoes([]);
      }
    } catch (error) {
      console.error("Erro ao carregar redações:", error);
      setRedacoes([]);

      if (error.response && error.response.status !== 401 && error.response.status !== 403) {
        toast.error("Erro ao carregar suas redações");
      }
    }
  };

  const loadRedacoesUERJ = async () => {
    if (!user || !user.id) {
      setRedacoesUERJ([]);
      return;
    }

    try {
      const response = await api.get("/minhas-redacoes/uerj");

      if (response.data && response.data.redacoes) {
        const redacoesFormatadas = response.data.redacoes.map((redacao) => ({
          id: redacao.id_redacao,
          aluno_nome: redacao.aluno_nome,
          tema_nome: redacao.tema_nome,
          classe_nome: redacao.classe_nome,
          status: redacao.status,
          url: redacao.url,

          adequacao_tema: redacao.adequacao_tema,
          tipo_texto: redacao.tipo_texto,
          argumentacao: redacao.argumentacao,
          periodo_coesao: redacao.periodo_coesao,
          modalidade: redacao.modalidade,

          corretor_id: redacao.corretor_id,
          revisor_id: redacao.revisor_id,

          enviada_date: redacao.enviada_date,
          corrigida_date: redacao.corrigida_date,
          extracted_text: redacao.extracted_text,
        }));

        setRedacoesUERJ(redacoesFormatadas);
      } else {
        setRedacoesUERJ([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar redações UERJ:", error);
      console.error("❌ Detalhes do erro:", error.response?.data);
      setRedacoesUERJ([]);

      if (error.response && error.response.status !== 401 && error.response.status !== 403) {
        toast.error("Erro ao carregar suas redações UERJ");
      }
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadRedacoes(), loadRedacoesUERJ()]);
    } catch (error) {
      console.error("Erro em loadAllData:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoadingUser && user && user.id) {
      loadAllData();
    } else if (!isLoadingUser && (!user || !user.id)) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingUser, user]);

  // =========================
  // Normalização (unificar ENEM + UERJ)
  // =========================

  const unified = useMemo(() => {
    const enem = (redacoes || []).map((r) => {
      // status a partir dos flags do backend
      const statusKey = r.corrigida ? "CORRIGIDA" : r.finalizada ? "AGUARDANDO" : "PENDENTE";
      const statusLabel =
        statusKey === "CORRIGIDA" ? "Corrigida" : statusKey === "AGUARDANDO" ? "Aguardando correção" : "Pendente";

      const isIA = r.tipo === "IA";

      const expired =
        !r.corrigida && !r.finalizada && r.entrega && r.hora ? validaDataClasse(r.entrega, `${r.hora}:00`) : false;

      // data principal do card: created_at (se existir), senão entrega (se existir)
      const primaryDate = r.created_at || (r.entrega ? `${r.entrega}T${r.hora || "00:00"}:00` : null);

      return {
        uid: `ENEM-${r.id}`,
        source: "ENEM",
        id: r.id,

        title: r.nome,
        turma: r.turma,

        isIA,
        tipoKey: isIA ? "IA" : "NORMAL",
        tipoLabel: isIA ? "IA" : "Normal",

        statusKey,
        statusLabel,

        createdAt: r.created_at,
        entrega: r.entrega,
        hora: r.hora,
        primaryDate,

        total: trazerTotalENEM(r),
        raw: r,

        // ações
        action: {
          corrigida: !!r.corrigida,
          finalizada: !!r.finalizada,
          expired,
        },
      };
    });

    const uerj = (redacoesUERJ || []).map((r) => {
      const statusKey = r.status === "CORRIGIDA" ? "CORRIGIDA" : r.status === "ENVIADA" ? "AGUARDANDO" : "OUTRO";
      const statusLabel =
        statusKey === "CORRIGIDA"
          ? "Corrigida"
          : statusKey === "AGUARDANDO"
          ? "Aguardando"
          : r.status || "Status";

      const primaryDate = r.enviada_date || r.corrigida_date || null;

      return {
        uid: `UERJ-${r.id}`,
        source: "UERJ",
        id: r.id,

        title: r.tema_nome || "Redação UERJ",
        turma: r.classe_nome,

        isIA: false,
        tipoKey: "UERJ",
        tipoLabel: "UERJ",

        statusKey,
        statusLabel,

        createdAt: r.enviada_date,
        entrega: null,
        hora: null,
        primaryDate,

        total: trazerTotalUERJ(r),
        raw: r,

        action: {
          corrigida: r.status === "CORRIGIDA",
          finalizada: r.status === "ENVIADA" || r.status === "CORRIGIDA",
          expired: false,
        },
      };
    });

    const all = [...enem, ...uerj];

    // ordenar por data (desc)
    all.sort((a, b) => {
      const da = a.primaryDate ? new Date(a.primaryDate).getTime() : 0;
      const db = b.primaryDate ? new Date(b.primaryDate).getTime() : 0;
      return db - da;
    });

    return all;
  }, [redacoes, redacoesUERJ]);

  // =========================
  // Filtros
  // =========================

  const filtered = useMemo(() => {
    // tipo
    const byType =
      tipoFiltro === "todos"
        ? unified
        : tipoFiltro === "ia"
        ? unified.filter((r) => r.source === "ENEM" && r.isIA)
        : tipoFiltro === "normal"
        ? unified.filter((r) => r.source === "ENEM" && !r.isIA)
        : tipoFiltro === "uerj"
        ? unified.filter((r) => r.source === "UERJ")
        : unified;

    // status
    const byStatus =
      statusFiltro === "todos"
        ? byType
        : statusFiltro === "corrigidas"
        ? byType.filter((r) => r.statusKey === "CORRIGIDA")
        : statusFiltro === "aguardando"
        ? byType.filter((r) => r.statusKey === "AGUARDANDO")
        : statusFiltro === "pendente"
        ? byType.filter((r) => r.statusKey === "PENDENTE")
        : byType;

    return byStatus;
  }, [unified, tipoFiltro, statusFiltro]);

  /*
  //logs de debug
  useEffect(() => {
    console.groupCollapsed("=== DEBUG FILTRAGEM (Minhas Redações) ===");
    console.log("📝 Total unificado:", unified.length);
    console.log("🎯 Filtro tipo:", tipoFiltro);
    console.log("📊 Filtro status:", statusFiltro);
    console.log("✅ Redações finais filtradas:", filtered.length);
    console.groupEnd();
  }, [unified.length, tipoFiltro, statusFiltro, filtered.length]);
  */

  // Render helpers (badges/ações)

  const getTipoBadge = (r) => {
    if (r.tipoKey === "IA") return { variant: "info", icon: "fa-robot", label: "IA" };
    if (r.tipoKey === "UERJ") return { variant: "warning", icon: "fa-university", label: "UERJ" };
    return { variant: "secondary", icon: "fa-user", label: "Normal" };
  };

  const getStatusBadge = (r) => {
    if (r.statusKey === "CORRIGIDA") return { variant: "success", icon: "fa-check-circle", label: r.statusLabel };
    if (r.statusKey === "AGUARDANDO") return { variant: "info", icon: "fa-clock", label: r.statusLabel };
    if (r.statusKey === "PENDENTE") return { variant: "warning", icon: "fa-exclamation-triangle", label: r.statusLabel };
    return { variant: "secondary", icon: "fa-question-circle", label: r.statusLabel };
  };

  const renderAction = (r) => {
    // UERJ
    if (r.source === "UERJ") {
      if (r.action.corrigida) {
        return (
          <S.ActionLink
            as={Link}
            to={`/visualizar-correcao-uerj/${r.id}`}
            data-variant="success"
            title="Ver correção"
          >
            <i className="fas fa-eye mr-2" />
            Ver correção
          </S.ActionLink>
        );
      }

      return (
        <S.ActionButton disabled data-variant="muted" title="Ainda não corrigida">
          <i className="fas fa-hourglass-half mr-2" />
          Em correção
        </S.ActionButton>
      );
    }

    // ENEM (Normal/IA)
    if (r.action.corrigida) {
      const to = r.isIA ? `/correcao-ia/${r.id}` : `/correcao/${r.id}`;
      return (
        <S.ActionLink as={Link} to={to} data-variant={r.isIA ? "info" : "success"} title="Ver correção">
          <i className={`fas ${r.isIA ? "fa-robot" : "fa-eye"} mr-2`} />
          {r.isIA ? "Ver correção IA" : "Ver correção"}
        </S.ActionLink>
      );
    }

    if (r.action.finalizada) {
      return (
        <S.ActionButton disabled data-variant="muted" title="Aguardando correção">
          <i className="fas fa-hourglass-half mr-2" />
          Aguardando
        </S.ActionButton>
      );
    }

    if (r.action.expired) {
      return (
        <S.ActionButton disabled data-variant="danger" title="Prazo expirado">
          <i className="fas fa-times-circle mr-2" />
          Expirado
        </S.ActionButton>
      );
    }

    return (
      <S.ActionButton
        data-variant="primary"
        onClick={() => {
          if (r.isIA) history.push("/enviar-redacao");
          else history.push(`/redacao/${r.id}`);
        }}
        title="Enviar redação"
      >
        <i className="fas fa-upload mr-2" />
        Enviar
      </S.ActionButton>
    );
  };

  const RedacaoCard = ({ r }) => {
    const tipo = getTipoBadge(r);
    const status = getStatusBadge(r);

    return (
      <S.CardShell>
        <S.CardTop>
          <S.CardTitle title={r.title}>{r.title}</S.CardTitle>

          <S.BadgesRow>
            <S.BadgePill data-variant={tipo.variant}>
              <i className={`fas ${tipo.icon} mr-2`} />
              {tipo.label}
            </S.BadgePill>

            <S.BadgePill data-variant={status.variant}>
              <i className={`fas ${status.icon} mr-2`} />
              {status.label}
            </S.BadgePill>
          </S.BadgesRow>
        </S.CardTop>

        <S.CardBody>
          <S.MetaGrid>
            <S.MetaItem>
              <S.MetaLabel>Total</S.MetaLabel>
              <S.MetaValue data-strong>{r.total != null ? String(r.total) : "-"}</S.MetaValue>
            </S.MetaItem>

            <S.MetaItem>
              <S.MetaLabel>Data</S.MetaLabel>
              <S.MetaValue>
                {r.source === "UERJ"
                  ? formatDateOnlyBR(r.raw?.enviada_date) + " • " + formatTimeBR(r.raw?.enviada_date)
                  : formatDateOnlyBR(r.createdAt) + " • " + formatTimeBR(r.createdAt)}
              </S.MetaValue>
            </S.MetaItem>

            <S.MetaItem>
              <S.MetaLabel>{r.source === "UERJ" ? "Classe" : "Turma"}</S.MetaLabel>
              <S.MetaValue>{r.turma || "---"}</S.MetaValue>
            </S.MetaItem>

            {r.source !== "UERJ" && (
              <S.MetaItem>
                <S.MetaLabel>Prazo</S.MetaLabel>
                <S.MetaValue>
                  {r.entrega ? `${r.entrega.split("-").reverse().join("/")} ${r.hora ? `• ${r.hora}` : ""}` : "---"}
                </S.MetaValue>
              </S.MetaItem>
            )}
          </S.MetaGrid>
        </S.CardBody>

        <S.CardFooter>{renderAction(r)}</S.CardFooter>
      </S.CardShell>
    );
  };

  // =========================
  // UI
  // =========================

  const isBusy = loading || isLoadingUser;

  return (
    <Template>
      <Container fluid style={{ maxWidth: "95%" }}>
        {/* Header */}
        <Row>
          <Col sm={12}>
            <S.PageHeader className="shadow-sm">
              <S.HeaderIcon>
                <i className="fas fa-file-alt" />
              </S.HeaderIcon>
              <div>
                <S.HeaderTitle>Minhas Redações</S.HeaderTitle>
                <S.HeaderSubtitle>Visualize e gerencie todas as suas redações</S.HeaderSubtitle>
              </div>
            </S.PageHeader>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col sm={12} md={4} className="mb-3 mb-md-0">
            <S.FilterCard className="shadow-sm">
              <S.FilterLabel>
                <i className="fas fa-filter mr-2 text-primary" />
                Filtrar por status
              </S.FilterLabel>

              <Form.Control
                as="select"
                value={statusFiltro}
                onChange={(e) => setStatusFiltro(e.target.value)}
                className="form-control-lg"
              >
                <option value="todos">Todos</option>
                <option value="corrigidas">Corrigidas</option>
                <option value="aguardando">Aguardando</option>
                <option value="pendente">Pendentes</option>
              </Form.Control>
            </S.FilterCard>
          </Col>

          <Col sm={12} md={4} className="mb-3 mb-md-0">
            <S.FilterCard className="shadow-sm">
              <S.FilterLabel>
                <i className="fas fa-tags mr-2 text-info" />
                Filtrar por tipo
              </S.FilterLabel>

              <Form.Control
                as="select"
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
                className="form-control-lg"
              >
                <option value="todos">Todos</option>
                <option value="normal">Redações normais</option>
                <option value="ia">IA</option>
                <option value="uerj">UERJ</option>
              </Form.Control>
            </S.FilterCard>
          </Col>

          <Col sm={12} md={4}>
            <S.CounterCard className="shadow-sm">
              <S.CounterNumber>{filtered.length}</S.CounterNumber>
              <S.CounterLabel>{filtered.length === 1 ? "Redação encontrada" : "Redações encontradas"}</S.CounterLabel>
            </S.CounterCard>
          </Col>
        </Row>

        {/* Content */}
        <Row>
          {isBusy ? (
            <Col className="text-center">
              <S.LoadingBox className="shadow-sm">
                <div className="mb-3">
                  <div className="spinner-grow text-primary mr-2" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div className="spinner-grow text-info mr-2" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div className="spinner-grow text-success mr-2" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
                <strong>CARREGANDO</strong>
              </S.LoadingBox>
            </Col>
          ) : (
            <>
              {filtered.length === 0 ? (
                <Col sm={12}>
                  <S.EmptyState className="shadow-sm">
                    <S.EmptyIcon>
                      <i className="fas fa-search" />
                    </S.EmptyIcon>

                    <S.EmptyTitle>
                      {statusFiltro === "todos"
                        ? "Nenhuma redação encontrada"
                        : statusFiltro === "corrigidas"
                        ? "Nenhuma redação corrigida encontrada"
                        : statusFiltro === "aguardando"
                        ? "Nenhuma redação aguardando encontrada"
                        : "Nenhuma redação pendente encontrada"}
                    </S.EmptyTitle>

                    <S.EmptyText>
                      {statusFiltro === "todos"
                        ? "Você ainda não possui redações por aqui."
                        : "Tente mudar os filtros para visualizar outros resultados."}
                    </S.EmptyText>

                    {/* CTA respeitando os “dois mundos” sem quebrar TESTE */}
                    {statusFiltro === "todos" && isPresencial && (
                      <Link to="/classe" className="btn btn-primary mt-3">
                        <i className="fas fa-plus mr-2" />
                        Ver atividades
                      </Link>
                    )}
                  </S.EmptyState>
                </Col>
              ) : (
                <>
                  {filtered.map((r) => (
                    <Col key={r.uid} sm={12} md={6} lg={4} className="mb-4">
                      <RedacaoCard r={r} />
                    </Col>
                  ))}
                </>
              )}
            </>
          )}
        </Row>
      </Container>
    </Template>
  );
}

export default MinhasRedacoes;